/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MYBROADCASTS_PHASE2_REFRESH_FUNCTION
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Provide refreshMine function to BroadcastsTabContent for "mine" tab.
 * Uses refreshMine from useBroadcastsList hook.
 *
 * File Size Check:
 * - Current: 267 lines
 * - Adding: ~10 lines
 * - Projected: 277 lines
 * - Decision: Proceed (table complexity justifies count)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/hooks/useBroadcastsList.ts
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabContent.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ALLBROADCASTS_SIDEBAR_FILTER
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Restructured to use sidebar layout matching Search.tsx pattern.
 * Route changed from /broadcasts to /allbroadcasts.
 * Left sidebar: BroadcastFilters component (collapsible horizontally)
 * Right area: Tabs + broadcast table + pagination.
 *
 * File Size Check:
 * - Current: 201 lines
 * - Adding: ~80 lines
 * - Removing: ~60 lines
 * - Projected: ~221 lines
 * - Decision: Proceed (sidebar complexity justifies slightly higher count, monitor for split)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabs.tsx
 * - client/src/features/broadcast/pages/broadcasts-list/utils/broadcastHelpers.ts
 * - client/src/components/broadcast/BroadcastFilters.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCASTS_FILTER_INTEGRATION
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Integrated BroadcastFilters with data flattening and filtering logic.
 * Extracted filters to BroadcastsFilterSection to maintain file size.
 *
 * File Size Check:
 * - Current: 180 lines
 * - Adding: ~40 lines
 * - Removing: ~10 lines
 * - Projected: ~210 lines (Splitting renderTabContent next if needed)
 * - Decision: Proceed with cautioned split.
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabs.tsx
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsFilterSection.tsx
 * - client/src/features/broadcast/pages/broadcasts-list/utils/broadcastHelpers.ts
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useMemo, useState } from "react";
import { useBroadcastsList } from "../hooks/useBroadcastsList";
import { BroadcastsTabContent } from "./BroadcastsTabContent";
// [2026-01-01] Filter integration
import { BroadcastFilters } from "../../../../../components/broadcast/BroadcastFilters";
import {
  flattenBroadcasts,
  filterFlattenedBroadcasts,
  paginateBroadcasts,
  calculateTotalPages
} from "../utils/broadcastHelpers";
import styles from "../BroadcastsListPage.module.scss";
// [2026-02-09] Mobile: FilterDrawer for mobile filter sidebar
import { useMediaQuery } from "../../../../../hooks/useMediaQuery";
import { FilterDrawer } from "../../../../../components/ui/FilterDrawer";

const BroadcastsTabs: React.FC = () => {
  // [2026-02-09] Mobile: Responsive detection and filter drawer state
  const { isMobile } = useMediaQuery();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const {
    activeTab,
    handleTabChange,
    recent,
    wts,
    wtb,
    mine,
    refreshMine, // [2025-01-02] Phase 2: Get refresh function from hook
    filtersCollapsed,
    toggleFilters,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    // [2026-01-01] Filter state from hook
    broadcastFilterKeyword,
    broadcastSKU,
    broadcastType,
    broadcastManufacturer,
    broadcastCategory,
    broadcastQty,
    broadcastCondition,
    broadcastCountry,
    broadcastCompany,
    handleFilterChange,
    handleClearFilters,
  } = useBroadcastsList();

  // [2026-01-01] Get current tab raw data
  const currentTabRawData = useMemo(() => {
    if (activeTab === "recent") return recent.data;
    if (activeTab === "wts") return wts.data;
    if (activeTab === "wtb") return wtb.data;
    return mine.data;
  }, [activeTab, recent.data, wts.data, wtb.data, mine.data]);

  // [2026-01-01] Flatten broadcasts for filtering (matches Search.tsx pattern)
  const allFlattenedBroadcasts = useMemo(() => {
    return flattenBroadcasts(currentTabRawData);
  }, [currentTabRawData]);

  // [2026-01-01] Apply filters to flattened broadcasts
  const filteredBroadcasts = useMemo(() => {
    return filterFlattenedBroadcasts(allFlattenedBroadcasts, {
      keyword: broadcastFilterKeyword,
      sku: broadcastSKU,
      type: broadcastType,
      manufacturer: broadcastManufacturer,
      category: broadcastCategory,
      qty: broadcastQty,
      condition: broadcastCondition,
      country: broadcastCountry,
      company: broadcastCompany,
    });
  }, [
    allFlattenedBroadcasts,
    broadcastFilterKeyword,
    broadcastSKU,
    broadcastType,
    broadcastManufacturer,
    broadcastCategory,
    broadcastQty,
    broadcastCondition,
    broadcastCountry,
    broadcastCompany,
  ]);

  // [2026-01-01] Reconstruct Broadcast[] from filtered items for table compatibility
  // We apply pagination here
  const paginatedItems = useMemo(() => {
    return paginateBroadcasts(filteredBroadcasts, currentPage, itemsPerPage);
  }, [filteredBroadcasts, currentPage, itemsPerPage]);

  const reconstructedBroadcasts = useMemo(() => {
    return paginatedItems.map(item => ({
      ...item.broadcastGroup,
      items: [item],
    })) as unknown as any[];
  }, [paginatedItems]);

  // [2026-01-01] Calculate pagination metadata
  const totalPages = useMemo(() => {
    return calculateTotalPages(filteredBroadcasts.length, itemsPerPage);
  }, [filteredBroadcasts.length, itemsPerPage]);

  // [2026-01-01] Get current loading/error state
  const currentLoading = activeTab === "recent" ? recent.loading :
                        activeTab === "wts" ? wts.loading :
                        activeTab === "wtb" ? wtb.loading :
                        mine.loading;

  const currentError = activeTab === "recent" ? recent.error :
                      activeTab === "wts" ? wts.error :
                      activeTab === "wtb" ? wtb.error :
                      mine.error;

  // [2026-02-09] Mobile: Count active filters for badge
  const activeFilterCount = [
    broadcastFilterKeyword,
    broadcastSKU,
    broadcastType,
    broadcastManufacturer,
    broadcastCategory,
    broadcastQty,
    broadcastCondition,
    broadcastCountry,
    broadcastCompany,
  ].filter(Boolean).length;

  // [2026-02-09] Mobile: Filter content component (reused in sidebar and drawer)
  const filterContent = (
    <BroadcastFilters
      searchQuery={broadcastFilterKeyword}
      skuFilter={broadcastSKU}
      typeFilter={broadcastType}
      manufacturerFilter={broadcastManufacturer}
      categoryFilter={broadcastCategory}
      qtyFilter={broadcastQty}
      conditionFilter={broadcastCondition}
      countryFilter={broadcastCountry}
      companyFilter={broadcastCompany}
      onSearchChange={(value) => handleFilterChange("broadcastFilterKeyword", value)}
      onSkuChange={(value) => handleFilterChange("broadcastSKU", value)}
      onTypeChange={(value) => handleFilterChange("broadcastType", value)}
      onManufacturerChange={(value) => handleFilterChange("broadcastManufacturer", value)}
      onCategoryChange={(value) => handleFilterChange("broadcastCategory", value)}
      onQtyChange={(value) => handleFilterChange("broadcastQty", value)}
      onConditionChange={(value) => handleFilterChange("broadcastCondition", value)}
      onCountryChange={(value) => handleFilterChange("broadcastCountry", value)}
      onCompanyChange={(value) => handleFilterChange("broadcastCompany", value)}
      onClearFilters={handleClearFilters}
      broadcastItems={filteredBroadcasts}
      allBroadcastItems={allFlattenedBroadcasts}
      collapsed={false}
      onToggleCollapse={() => {}}
    />
  );

  return (
    <div className={`container-fluid ${styles.fullWidthContainer}`}>
      {/* [2026-02-09] Mobile: Filter button and drawer */}
      {isMobile && (
        <>
          <div className="d-flex justify-content-end mb-3 px-3">
            <button
              className={`filter-button ${activeFilterCount > 0 ? "has-filters" : ""}`}
              onClick={() => setFilterDrawerOpen(true)}
            >
              <svg className="filter-button__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="filter-button__count">{activeFilterCount}</span>
              )}
            </button>
          </div>
          <FilterDrawer
            isOpen={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            title="Filters"
            activeFilterCount={activeFilterCount}
            onClear={handleClearFilters}
            onApply={() => setFilterDrawerOpen(false)}
          >
            {filterContent}
          </FilterDrawer>
        </>
      )}

      <div className={`row g-0 ${styles.stretchRow}`}>
        {/* [2026-01-01] Left Sidebar - BroadcastFilters (hidden on mobile) */}
        {!isMobile && (
          <aside
            className={`${
              filtersCollapsed ? "col-1" : "col-12 col-md-4 col-lg-3"
            } ps-3 pe-3 d-flex flex-column ${filtersCollapsed ? styles.filtersSidebarCollapsed : styles.filtersSidebar}`}
          >
            <BroadcastFilters
              searchQuery={broadcastFilterKeyword}
              skuFilter={broadcastSKU}
              typeFilter={broadcastType}
              manufacturerFilter={broadcastManufacturer}
              categoryFilter={broadcastCategory}
              qtyFilter={broadcastQty}
              conditionFilter={broadcastCondition}
              countryFilter={broadcastCountry}
              companyFilter={broadcastCompany}
              onSearchChange={(value) => handleFilterChange("broadcastFilterKeyword", value)}
              onSkuChange={(value) => handleFilterChange("broadcastSKU", value)}
              onTypeChange={(value) => handleFilterChange("broadcastType", value)}
              onManufacturerChange={(value) => handleFilterChange("broadcastManufacturer", value)}
              onCategoryChange={(value) => handleFilterChange("broadcastCategory", value)}
              onQtyChange={(value) => handleFilterChange("broadcastQty", value)}
              onConditionChange={(value) => handleFilterChange("broadcastCondition", value)}
              onCountryChange={(value) => handleFilterChange("broadcastCountry", value)}
              onCompanyChange={(value) => handleFilterChange("broadcastCompany", value)}
              onClearFilters={handleClearFilters}
              broadcastItems={filteredBroadcasts}
              allBroadcastItems={allFlattenedBroadcasts}
              collapsed={filtersCollapsed}
              onToggleCollapse={toggleFilters}
            />
          </aside>
        )}

        {/* [2026-01-01] Right Content Area - Tabs + Table (full width on mobile) */}
        <div
          className={`${
            isMobile ? "col-12" : filtersCollapsed ? "col-11" : "col-12 col-md-8 col-lg-9"
          } bg-white border-bottom border-start-0 d-flex flex-column`}
        >
          {/* [2025-12-31] Tab navigation */}
          <ul className={`nav nav-tabs px-3 ${styles.tabsNav}`}>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "recent" ? "active" : ""}`}
                onClick={() => handleTabChange("recent")}
              >
                Recent
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "wts" ? "active" : ""}`}
                onClick={() => handleTabChange("wts")}
              >
                WTS
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "wtb" ? "active" : ""}`}
                onClick={() => handleTabChange("wtb")}
              >
                WTB
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "mine" ? "active" : ""}`}
                onClick={() => handleTabChange("mine")}
              >
                My Broadcasts
              </button>
            </li>
          </ul>

          {/* [2025-12-31] Tab content area with filtered/paginated data */}
          <div className={`p-3 ${styles.contentArea}`}>
            <BroadcastsTabContent
              data={reconstructedBroadcasts}
              loading={currentLoading}
              error={currentError}
              tabKey={activeTab}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredBroadcasts.length}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
              handleItemsPerPageChange={handleItemsPerPageChange}
              refreshCallback={activeTab === "mine" ? refreshMine : undefined} // [2025-01-02] Phase 2: Only for mine tab
              loadingMessage={activeTab === "wts" ? "Loading WTS broadcasts…" :
                             activeTab === "wtb" ? "Loading WTB broadcasts…" :
                             "Loading broadcasts..."}
              showMobileFooter={true}
              showMobileActions={activeTab === "mine"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastsTabs;
